import type { CustomDriver } from '@w/db-custom'
import type { createFlashcardsRpcOptions, refreshAfterDirectoryCommands } from './flashcards-rpc-options'
import type { PageCommitPolicy, createWriteGate } from '../db/page-commit-buffer'
import type { SessionOwner } from '@w/auth-fe'
import type { QueryClient } from '@w/react-query'
import type { createAdoptBumpErrors } from '../lexicon/adopt-bump-errors'
import type { Block } from '@w/content-blocks'
import type { SpeechStore } from '../listen/speech-session'
import type { Change, Doc } from '@w/content-blocks-editor/doc-bindings-for-page'
import type { BlockEditorHandle } from '@w/content-blocks-editor'
import type { createClientDb } from '../db/db'
import type { PageEmbeddingsBrowserClient } from '../search/page-embeddings-browser.client'
import type { FlashcardsMediaClient } from '../media/flashcards-media.client'
import type { createAuthClient } from '../flashcards-auth'
import type { FlashcardsUser } from '../flashcards-auth'
import type { FrequencyListPort } from '../lexicon/vocabulary-progress'
import type { UseAudioOptions } from '@w/react-audio'
import type { Tel } from '@w/tel'
import type { MoveInput } from './page-commands'
import type { SpacesResources } from './spaces-resources'
import type { PageSearchEmbeddings } from '../search/page-search'
import type { ServerEmbeddings } from '../search/server-embeddings.client'
import type { WorkspaceRealtimeClient } from './realtime-clients'

export type FlashcardsDb = ReturnType<typeof createClientDb>
export type InitFlag = 'root' | `authenticated-layout:${string}`

/** Time source for countdowns; tests inject a manual one instead of faking global timers. */
export interface Clock {
  now(): number
  /** Runs `run` every `ms`; returns the cancel. */
  every(run: () => void, ms: number): () => void
}

export const systemClock: Clock = {
  now: () => Date.now(),
  every: (run, ms) => {
    const interval = setInterval(run, ms)
    return () => { clearInterval(interval) }
  },
}

export interface FlashcardsTestSupport {
  beforeCreatePage?: (pageId: string) => Promise<void>
  registerEditor?: (info: { pageId: string; editor: BlockEditorHandle; doc: Doc; initialSnapshot: Uint8Array }) => void
  recordChange?: (change: Change) => void
  defaultPage?: {
    translatable?: true
    readonly?: true
    title?: string
    blocks?: Block[]
  }
  afterDefaultPageCreated?: (
    ctx: FlashcardsFeRouterCtx,
    page: { id: string; workspaceId: string },
  ) => Promise<void>
  audioOptions?: UseAudioOptions
  trainingOptionRandom?: () => number
}

export interface FlashcardsFeRouterCtx {
  tel: Tel
  queryClient: QueryClient
  rpc: ReturnType<typeof createFlashcardsRpcOptions>
  rpcClient: ReturnType<typeof refreshAfterDirectoryCommands>
  auth: ReturnType<typeof createAuthClient>
  fetchFn: Window['fetch']
  serverEmbeddings?: ServerEmbeddings
  searchEmbeddings: PageSearchEmbeddings
  close(): Promise<void>
  localStorage: Storage
  deviceId: string
  clock: Clock
  initFlags: Set<InitFlag>
  getUser: () => FlashcardsUser
  defaultNativeLanguage: string
  /** Voiced listening chunks on this device (`page-listening.spec.md`). */
  speechStore: SpeechStore
  _ensureUserResources?: (startupTel: Tel) => void | Promise<void>
  openWorkspace?(this: void, workspaceId: string): Promise<void>
  closeWorkspace?(this: void, workspaceId: string): Promise<void>
  pageCommit: PageCommitPolicy
  writes: ReturnType<typeof createWriteGate>
  /** Adoption request errors that arrive after navigation, keyed by adopted page id (flashcards-lexicon.spec.md:501-504: shown inline). Lives and dies with this ctx. */
  adoptBumpErrors: ReturnType<typeof createAdoptBumpErrors>
  /** Owner of the signed-in user's resources: render snapshot, holder, deferred release (@w/auth-fe session-owner). */
  session: SessionOwner<SpacesResources>
  afterCommit(this: void, workspaceId: string): void | Promise<void>
  afterLocalCommit?(this: void, spaceId: string): void | Promise<void>
  moveSubtree(input: MoveInput): Promise<void>
  spaces: SpacesResources
  customDb(spaceId: string): CustomDriver
  db(spaceId: string): FlashcardsDb
  embeddings(spaceId: string): PageEmbeddingsBrowserClient | undefined
  afterDirectoryCommand(): Promise<void>
  bucket: FlashcardsMediaClient
  /** Live collaboration for a workspace (page-collaboration.spec.md): a client only for a signed-in platform admin, else null. */
  realtime?(this: void, workspaceId: string): WorkspaceRealtimeClient | null
  frequencyList: FrequencyListPort
  testSupport?: FlashcardsTestSupport
}
